import { AudioManager } from './AudioManager';

/**
 * ゲーム効果音（全て手続き型生成）
 * - エンジン/アフターバーナー（連続）
 * - バルカン砲（連続ループ）
 * - ミサイル発射
 * - 爆発（小/大）
 * - ロックオン警告ビープ
 * - 被弾音
 */
export class SoundEffects {
  private am: AudioManager;

  // エンジン音（連続）
  // sawtooth廃止 → ノイズ3層 + タービンホワイン(sine)
  private engineGain!: GainNode;
  private engineLowFilter!: BiquadFilterNode;   // 低域ランブル
  private engineMidFilter!: BiquadFilterNode;   // 中域ジェット音
  private engineWhineOsc!: OscillatorNode;      // 高域タービンホワイン
  private engineWhineGain!: GainNode;
  private afterburnGain!: GainNode;
  private engineStarted = false;

  // バルカン音（連続ループ）
  private vulcanNode: AudioBufferSourceNode | null = null;
  private vulcanGain!: GainNode;

  // ロックオンビープ状態
  private lockBeepActive = false;
  private lockBeepTimer = 0;

  // 脅威警告状態
  private threatWarnTimer = 0;

  // ノイズバッファキャッシュ
  private noiseBuffer1s!: AudioBuffer;
  private noiseBuffer100ms!: AudioBuffer;

  constructor(am: AudioManager) {
    this.am = am;
    this.noiseBuffer1s   = am.createNoiseBuffer(1.0);
    this.noiseBuffer100ms = am.createNoiseBuffer(0.1);
    this.buildEngine();
    this.buildVulcanChain();
  }

  // ----------------------------------------------------------------
  // エンジン音（ジェット機風: ノイズ3層 + タービンホワイン）
  // ----------------------------------------------------------------
  private buildEngine(): void {
    const am  = this.am;

    // マスターゲイン（0から startEngine() で上げる）
    this.engineGain = am.createGain(0);
    this.engineGain.connect(am.master);

    // フィルターだけ先に作成（ソースの接続・startは startEngine() で行う）
    this.engineLowFilter = am.createFilter('bandpass', 90, 1.2);
    this.engineLowFilter.connect(this.engineGain);

    this.engineMidFilter = am.createFilter('bandpass', 380, 0.8);

    this.engineWhineOsc  = am.createOsc('sine', 2200);
    this.engineWhineGain = am.createGain(0); // 0で開始（startEngineまで無音）
    this.engineWhineOsc.connect(this.engineWhineGain);
    this.engineWhineGain.connect(am.master);

    this.afterburnGain = am.createGain(0);
    this.afterburnGain.connect(am.master);
  }

  startEngine(): void {
    if (this.engineStarted) return;
    this.engineStarted = true;

    const am  = this.am;
    const ctx = am.ctx;

    // ノイズソースをここで生成・start（ユーザー操作後なので安全）
    const noiseBuf = am.createNoiseBuffer(2.0);

    // 層1: 低域ランブル
    const lowNoise = ctx.createBufferSource();
    lowNoise.buffer = noiseBuf; lowNoise.loop = true;
    lowNoise.connect(this.engineLowFilter);
    lowNoise.start();

    // 層2: 中域ジェット音
    const midNoise = ctx.createBufferSource();
    midNoise.buffer = am.createNoiseBuffer(1.5); midNoise.loop = true;
    const midGain = am.createGain(0.55);
    midNoise.connect(this.engineMidFilter);
    this.engineMidFilter.connect(midGain);
    midGain.connect(this.engineGain);
    midNoise.start();

    // 層3: タービンホワイン
    this.engineWhineOsc.start();

    // アフターバーナー
    const abFilter = am.createFilter('bandpass', 1200, 0.4);
    const abNoise  = ctx.createBufferSource();
    abNoise.buffer = am.createNoiseBuffer(1.0); abNoise.loop = true;
    abNoise.connect(abFilter);
    abFilter.connect(this.afterburnGain);
    abNoise.start();

    // フェードイン
    this.engineGain.gain.setTargetAtTime(0.3, am.now, 0.3);
    this.engineWhineGain.gain.setTargetAtTime(0.04, am.now, 0.5);
  }

  /**
   * フレームごとに呼ぶ。speed: km/h, boost: boolean
   */
  updateEngine(speed: number, boost: boolean): void {
    const t   = this.am.now;
    const spd = Math.max(150, Math.min(speed, 1300));
    const ratio = (spd - 150) / (950 - 150); // 0〜1 (通常速度範囲)

    // 低域: 速度でフィルター帯域を上げる（エンジン回転数感）
    this.engineLowFilter.frequency.setTargetAtTime(70 + ratio * 60, t, 0.15);

    // 中域: 速度で帯域を移動
    this.engineMidFilter.frequency.setTargetAtTime(300 + ratio * 250, t, 0.15);

    // タービンホワイン: 速度で音程上昇
    this.engineWhineOsc.frequency.setTargetAtTime(1800 + ratio * 1400, t, 0.2);
    this.engineWhineGain.gain.setTargetAtTime(0.02 + ratio * 0.05, t, 0.15);

    // 全体音量（速度依存）
    this.engineGain.gain.setTargetAtTime(0.2 + ratio * 0.15, t, 0.15);

    // アフターバーナー
    const abTarget = boost ? 0.45 : 0;
    this.afterburnGain.gain.setTargetAtTime(abTarget, t, boost ? 0.06 : 0.35);
  }

  // ----------------------------------------------------------------
  // バルカン砲
  // ----------------------------------------------------------------
  private buildVulcanChain(): void {
    this.vulcanGain = this.am.createGain(0);
    this.vulcanGain.connect(this.am.master);
  }

  startVulcan(): void {
    if (this.vulcanNode) return;

    const ctx = this.am.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer1s;
    src.loop = true;

    // バルカン=高域ノイズ + 低域パルス
    const hp  = this.am.createFilter('highpass', 1500, 1);
    const bp  = this.am.createFilter('bandpass', 300, 8);

    src.connect(hp);
    src.connect(bp);
    hp.connect(this.vulcanGain);
    bp.connect(this.vulcanGain);
    src.start();
    this.vulcanNode = src;
    this.vulcanGain.gain.setTargetAtTime(0.35, this.am.now, 0.02);
  }

  stopVulcan(): void {
    if (!this.vulcanNode) return;
    this.vulcanGain.gain.setTargetAtTime(0, this.am.now, 0.04);
    const node = this.vulcanNode;
    this.vulcanNode = null;
    setTimeout(() => { try { node.stop(); } catch { /* already stopped */ } }, 200);
  }

  // ----------------------------------------------------------------
  // ミサイル発射
  // ----------------------------------------------------------------
  playMissileLaunch(): void {
    const am = this.am;
    const t  = am.now;

    const g = am.createGain(0.5);
    g.connect(am.master);

    // ピッチダウンスイープ（シュー）
    const osc = am.createOsc('sawtooth', 600);
    osc.frequency.linearRampToValueAtTime(80, t + 0.4);
    osc.connect(g);
    osc.start(t);
    osc.stop(t + 0.45);

    // ノイズ層
    const ns  = am.createNoise(this.noiseBuffer100ms);
    const hp  = am.createFilter('bandpass', 400, 0.8);
    const ng  = am.createGain(0.3);
    ns.connect(hp); hp.connect(ng); ng.connect(am.master);
    ng.gain.setTargetAtTime(0, t + 0.05, 0.05);
    ns.start(t);

    g.gain.setTargetAtTime(0, t + 0.1, 0.12);
  }

  // ----------------------------------------------------------------
  // 爆発
  // ----------------------------------------------------------------
  playExplosionSmall(): void {
    this.playExplosion(0.35, 0.3, 600);
  }

  playExplosionLarge(): void {
    this.playExplosion(0.7, 0.8, 120);
  }

  private playExplosion(volume: number, duration: number, filterFreq: number): void {
    const am = this.am;
    const t  = am.now;

    const g  = am.createGain(volume);
    g.connect(am.master);
    g.gain.setTargetAtTime(0, t + 0.02, duration * 0.3);

    const lp = am.createFilter('lowpass', filterFreq, 1);
    lp.connect(g);

    const ns = am.createNoise(this.noiseBuffer1s);
    ns.connect(lp);
    ns.start(t);
    ns.stop(t + duration + 0.1);

    // 低音ドン
    const osc = am.createOsc('sine', filterFreq * 0.4);
    const og  = am.createGain(volume * 0.6);
    osc.connect(og); og.connect(am.master);
    osc.start(t); osc.stop(t + duration * 0.5);
    og.gain.setTargetAtTime(0, t + 0.01, duration * 0.15);
  }

  // ----------------------------------------------------------------
  // ロックオンビープ（フレームごとに呼ぶ）
  // ----------------------------------------------------------------
  updateLockOn(locked: boolean, delta: number): void {
    if (!locked) { this.lockBeepActive = false; this.lockBeepTimer = 0; return; }

    this.lockBeepTimer -= delta;
    if (this.lockBeepTimer <= 0) {
      this.lockBeepTimer = locked ? 0.2 : 0.8; // ロック済みは速くビープ
      this.playBeep(880, 0.08, 0.06);
    }
  }

  private playBeep(freq: number, duration: number, volume: number): void {
    const am = this.am;
    const t  = am.now;
    const osc = am.createOsc('sine', freq);
    const g   = am.createGain(volume);
    osc.connect(g); g.connect(am.master);
    osc.start(t); osc.stop(t + duration);
    g.gain.setTargetAtTime(0, t + duration * 0.5, 0.02);
  }

  // ----------------------------------------------------------------
  // 脅威警告音（フレームごとに呼ぶ）
  // ----------------------------------------------------------------
  updateThreatWarning(lockedByEnemy: boolean, incomingMissile: boolean, delta: number): void {
    if (!lockedByEnemy && !incomingMissile) {
      this.threatWarnTimer = 0;
      return;
    }

    this.threatWarnTimer -= delta;
    if (this.threatWarnTimer <= 0) {
      if (incomingMissile) {
        // ミサイル警告: 高頻度の高音ビープ
        this.threatWarnTimer = 0.15;
        this.playBeep(1200, 0.06, 0.12);
      } else if (lockedByEnemy) {
        // ロックオン警告: 低頻度の中音ビープ
        this.threatWarnTimer = 0.5;
        this.playBeep(600, 0.1, 0.08);
      }
    }
  }

  // ----------------------------------------------------------------
  // ミサイル被弾音
  // ----------------------------------------------------------------
  playMissileHit(): void {
    const am = this.am;
    const t  = am.now;
    // 重い爆発 + 画面揺れ用の低音
    this.playExplosion(0.8, 0.6, 100);
    // 金属音
    const osc = am.createOsc('square', 200);
    const g   = am.createGain(0.3);
    osc.frequency.linearRampToValueAtTime(60, t + 0.3);
    osc.connect(g); g.connect(am.master);
    osc.start(t); osc.stop(t + 0.35);
    g.gain.setTargetAtTime(0, t + 0.05, 0.1);
  }

  // ----------------------------------------------------------------
  // 被弾音
  // ----------------------------------------------------------------
  playHit(): void {
    const am = this.am;
    const t  = am.now;
    const osc = am.createOsc('sawtooth', 120);
    const g   = am.createGain(0.4);
    osc.connect(g); g.connect(am.master);
    osc.start(t); osc.stop(t + 0.15);
    g.gain.setTargetAtTime(0, t + 0.03, 0.05);
  }
}
